import React, { useEffect, useMemo, useState } from 'react'
import { Grid, MenuItem, FormControlLabel, CircularProgress } from '@material-ui/core'
import InputMask from 'react-input-mask'
import { timezones } from 'lib/constants/timezones'
import { StyledComponents } from 'components/StyledComponents'
import {
  ConditionalRender,
  StyledFormControl,
  StyledInputLabel,
  StyledSelect,
  StyledSubHeader,
} from 'components/widgets'
import { Organization, trans, TranslationGroup, TranslationKey } from 'lib/types'
import { flattenOrg, FlattenedOrg, unflattenOrg } from 'models/Orgs'
import { PopUpNotifications } from 'models'
import { validateOrgData } from 'lib/utils'
import {
  ActionsWrapper,
  ConfirmButton,
  StyledButton,
  SectionWrapper,
} from 'components/partial-pages/GeneralSettingsPage/Common'
import { getOrganization, updateOrganization } from 'models/api'

import { SubscriptionSection } from './SubscriptionSection'
import { VoiceSettingsSubsection } from './VoiceSettingsSubsection'
import { EssentialOnlyButtonsSection } from 'components/partials/EssentialOnlyButtonsSection'
import { isSuperAdmin } from 'lib/utils/auth'
import { essentialOnlyButtonsFlag } from 'lib/utils/featureFlags'
import { useGlobal } from 'contexts/GlobalProvider'
import { ToggleSwitch, SwitchLabel } from 'components/partials/MappedInIntegrationForm'

const { find } = require('art-comprehensions')

type Tier = 'essential' | 'elite' | 'ultimate'
type SubStatus = 'paid' | 'delinquent' | 'suspended'

type FlattenedOrgWithSub = FlattenedOrg & {
  subscriptionTier?: Tier
  subscriptionStatus?: SubStatus
  zelloUserMax?: number | ''
  zelloSubdomain?: string
  zelloUsername?: string
  zelloPassword?: string
}

export const OrganizationDetailsForm = ({
  organization,
  editAsGroup,
}: {
  organization: Organization
  editAsGroup?: boolean
}) => {
  const translation: TranslationGroup = trans.merge(TranslationKey.ADMIN)
  const { singlePressActivationEnabled, setSinglePressActivationEnabled, selectedOrg, setSelectedOrg, setRefreshFlag } =
    useGlobal()
  const [savedOrg, setSavedOrg] = useState<FlattenedOrgWithSub | undefined>()
  const [unsavedOrg, setUnsavedOrg] = useState<FlattenedOrgWithSub | undefined>()
  const [isChanged, setIsChanged] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [tzOffset, setTzOffset] = useState<number | undefined>()
  const propertyType = editAsGroup ? translation.group : translation.property

  useEffect(() => {
    getOrganization(organization.id).then(org => {
      const flat = flattenOrg(org) as FlattenedOrgWithSub
      setSavedOrg(flat)
      setUnsavedOrg(flat)
      setTzOffset(org?.tzOffset)
      return org
    })
  }, [organization.id])

  function applyEdit(field: keyof FlattenedOrgWithSub, value: any) {
    if (!unsavedOrg || !savedOrg) return
    if (value?.toString?.().length >= 255) return

    const newUnsavedOrg: FlattenedOrgWithSub = { ...unsavedOrg, [field]: value }
    setUnsavedOrg(newUnsavedOrg)

    const errorMessage = validateOrgData(newUnsavedOrg as any)
    PopUpNotifications.reset()

    if (errorMessage) {
      PopUpNotifications.fireError({ content: errorMessage })
    } else {
      const hasChanges = !!find(
        newUnsavedOrg,
        // @ts-ignore
        (v, key) => v !== savedOrg[key],
      )
      setIsChanged(hasChanges)
    }
  }

  function handleFormCancel() {
    if (!savedOrg) return
    setUnsavedOrg(savedOrg)
    setIsChanged(false)
    setTzOffset((savedOrg as any)?.tzOffset)
  }

  async function handleFormSubmit() {
    if (!unsavedOrg || saving) return

    setSaving(true)
    try {
      await updateOrganization(unflattenOrg(unsavedOrg as any))
      const org = await getOrganization(organization.id)
      const flat = flattenOrg(org) as FlattenedOrgWithSub

      setSavedOrg(flat)
      setUnsavedOrg(flat)
      setTzOffset(org?.tzOffset)
      setIsChanged(false)

      // Reflect the changes (e.g. a tier downgrade) across the app: keep the
      // active org current and reload channels / device groups so the Voice
      // page picks up the new tier and collapses any leftover groups.
      if (selectedOrg?.id === org.id) setSelectedOrg(org)
      setRefreshFlag(true)

      PopUpNotifications.fireSuccess({ content: `${propertyType} ${translation.updated}` })
    } catch (e) {
      PopUpNotifications.fireError({ content: `Failed to update ${propertyType.toLowerCase()}. Please try again.` })
    } finally {
      setSaving(false)
    }
  }

  function getCommonProps(field: keyof FlattenedOrgWithSub) {
    return {
      id: `org-${String(field)}`,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        return applyEdit(field, (e.target as HTMLInputElement).value)
      },
      value: unsavedOrg ? (unsavedOrg[field] as any) || '' : '',
      inputProps: { maxLength: 255 },
    }
  }

  // Groups carry the same details properties do: the API stores these props on
  // every org whatever its type.
  const formFields = [
    [
      [translation.name, 'name'],
      [translation.phone, 'phone', '(999) 999-9999'],
    ],
    [
      [translation.proximity_uuid, 'proximityUuidValue', '********-****-****-****-************'],
      [translation.proximity_uuid_friendly_name, 'proximityUuidName'],
    ],
    [
      [translation.address_line_1, 'street1'],
      [translation.address_line_2, 'street2'],
    ],
    [
      [translation.city, 'city'],
      [translation.state, 'state'],
    ],
    [
      [translation.zipcode, 'zipcode'],
      [translation.country, 'country'],
    ],
  ]

  const subscriptionTierValue = (unsavedOrg?.subscriptionTier ?? '') as string
  const subscriptionStatusValue = (unsavedOrg?.subscriptionStatus ?? '') as string

  // Build a live org object for VoiceSettings to read config state
  const liveOrgForVoice = useMemo<Organization | null>(() => {
    if (!unsavedOrg) return null
    return unflattenOrg(unsavedOrg as any) as any
  }, [unsavedOrg])

  const zelloUserMaxValue = (unsavedOrg?.zelloUserMax ?? (liveOrgForVoice as any)?.zelloUserMax ?? '') as number | ''

  return (
    <SectionWrapper>
      <StyledSubHeader>{`${propertyType} ${translation.details}`}</StyledSubHeader>

      <form>
        {formFields.map(line => (
          <Grid key={`line-${line[0][0]}`} container spacing={2}>
            {line.map(([label, field, mask]) => (
              <Grid key={`item-${field}`} item xs={6}>
                <StyledFormControl>
                  <StyledInputLabel htmlFor={`org-${field}`}>{label}</StyledInputLabel>
                  {mask ? (
                    <InputMask
                      mask={mask as string}
                      maskPlaceholder={null}
                      {...getCommonProps(field as keyof FlattenedOrgWithSub)}
                    >
                      {() => <StyledComponents.StyledInput />}
                    </InputMask>
                  ) : (
                    <StyledComponents.StyledInput {...getCommonProps(field as keyof FlattenedOrgWithSub)} />
                  )}
                </StyledFormControl>
              </Grid>
            ))}
          </Grid>
        ))}
        {/* Timezone */}
        <StyledFormControl style={{ width: '49%', marginTop: 6 }}>
          <StyledSubHeader id="timezone-select-label">Timezone</StyledSubHeader>
          <StyledSelect
            labelId="timezone-select-label"
            id="timezone-select"
            value={tzOffset?.toString() || ''}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
              const selectedOffset = Number(e.target.value)
              setTzOffset(selectedOffset)
              applyEdit('tzOffset' as keyof FlattenedOrgWithSub, selectedOffset)
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {Object.entries(timezones).map(([tz, offset]) => (
              <MenuItem key={tz} value={offset.toString()}>
                {tz} (UTC{offset >= 0 ? `+${offset / 3600}` : offset / 3600})
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
        {/* Subscription and voice are org-level, not property-level: the API keeps
            tier, status and Zello config on every org whatever its type, so groups
            get the same section properties do. */}
        <ConditionalRender condition={isSuperAdmin()}>
          <SubscriptionSection
            subscriptionTierValue={subscriptionTierValue}
            subscriptionStatusValue={subscriptionStatusValue}
            onChangeTier={tier => applyEdit('subscriptionTier', tier)}
            onChangeStatus={status => applyEdit('subscriptionStatus', status)}
          >
            {/* ✅ Voice settings lives under Subscription */}
            <VoiceSettingsSubsection
              org={liveOrgForVoice}
              voiceBackend={(unsavedOrg as any)?.voiceBackend}
              zelloProvisioningStatus={(unsavedOrg as any)?.zelloProvisioningStatus}
              zelloUserMaxValue={zelloUserMaxValue}
              onChangeZelloUserMax={v => applyEdit('zelloUserMax', v)}
              onOrgUpdated={updated => {
                // The wizard and the fleet toggle return the org they just saved.
                // Merge it into both saved and unsaved state, so those fields
                // don't count as pending edits for Apply Changes.
                const flatUpdated = flattenOrg(updated) as any
                const nextSaved = { ...(savedOrg as any), ...flatUpdated }
                const next = { ...(unsavedOrg as any), ...flatUpdated }
                setSavedOrg(nextSaved)
                setUnsavedOrg(next)
                // re-evaluate changed state
                const hasChanges = !!find(
                  next,
                  // @ts-ignore
                  (val, key) => val !== nextSaved?.[key],
                )
                setIsChanged(hasChanges)
                // As after Apply Changes: the sidebar and Voice pages read the
                // active org, so a newly enabled voice shows up without a reload.
                if (selectedOrg?.id === updated?.id) setSelectedOrg(updated)
                setRefreshFlag(true)
              }}
            />

            {/* Excluding a button is a live mutation, like a license: it revokes
                server-side on click rather than waiting for Apply Changes. */}
            <ConditionalRender condition={essentialOnlyButtonsFlag}>
              <EssentialOnlyButtonsSection org={liveOrgForVoice} />
            </ConditionalRender>
          </SubscriptionSection>
        </ConditionalRender>

        {/* Session-only control (not persisted): reveals the per-device
            "Single Press Activation" toggles for qualifying devices. */}
        <ConditionalRender condition={isSuperAdmin() && !editAsGroup}>
          <StyledFormControl style={{ marginTop: 12 }}>
            <StyledSubHeader>Single Press Activation</StyledSubHeader>
            <FormControlLabel
              style={{ marginLeft: 0 }}
              control={
                <ToggleSwitch
                  checked={singlePressActivationEnabled}
                  onChange={(_, checked) => setSinglePressActivationEnabled(checked)}
                  name="singlePressActivationEnabled"
                />
              }
              label={
                <SwitchLabel>
                  {singlePressActivationEnabled ? 'Enabled' : 'Disabled'} — session only, not saved
                </SwitchLabel>
              }
            />
          </StyledFormControl>
        </ConditionalRender>

        <ActionsWrapper style={{ marginTop: 16, display: 'flex', alignItems: 'center' }}>
          <StyledButton onClick={handleFormCancel} disabled={saving}>
            {translation.cancel}
          </StyledButton>
          <ConfirmButton disabled={!isChanged || saving} onClick={handleFormSubmit}>
            {translation.apply_changes}
          </ConfirmButton>
          {saving && <CircularProgress size={20} thickness={5} style={{ marginLeft: 12 }} />}
        </ActionsWrapper>
      </form>
    </SectionWrapper>
  )
}
